/* In-app (bell) alerts — API layer over InAppAlertsController (/api/v1/notifications/inbox).
 * Every call is scoped server-side to the signed-in user. The poll uses silentLoading so the
 * global loader never flashes every 30 s. */
import { api } from '../../../api/client';
import type { AlertPopupSettings } from './alertPopupSettings';

export interface InAppAlertDetail {
  jobId?: string | null; sampleId?: string | null; parameter?: string | null; result?: string | null;
  units?: string | null; limit: string; batch?: string | null; permit?: string | null; client?: string | null;
}

export interface InAppAlert {
  id: string;
  ruleCode: string;
  ruleName: string;
  severity?: 'critical' | 'warning' | 'info' | null;
  ackRequired: boolean;
  triggerId?: string | null;
  triggerLabel?: string | null;
  subject: string;
  summary?: string | null;
  createdAt: string;
  readAt?: string | null;
  ackAt?: string | null;
  /** Auto-resolved (its condition cleared): no longer needs Acknowledge, never popped up again. */
  resolvedAt?: string | null;
  details: InAppAlertDetail[];
  /** The pop-up setting that applies to this alert (its rule's setting, else the Default). */
  popup?: AlertPopupSettings | null;
  /** Client-only: the Pop-up Settings "Show test pop-up" sample — never sent to the API. */
  preview?: boolean;
}

export interface InAppAlertPage { items: InAppAlert[]; total: number; unread: number; page: number; pageSize: number }

export interface InAppAlertSummary { unread: number; awaitingAck: number; popups: InAppAlert[]; nextSince: string }

const BASE = '/api/v1/notifications/inbox';
const quiet = { silentLoading: true } as never;

/** The poll. Omit `since` on the first poll after sign-in. */
export async function getSummary(since: string | null): Promise<InAppAlertSummary> {
  const res = await api.get(`${BASE}/summary`, { ...(quiet as object), params: since ? { since } : {} } as never);
  return (res.data as { data: InAppAlertSummary }).data;
}

/** The user's alerts, newest first (the bell list). */
export async function getAlerts(page: number, pageSize: number): Promise<InAppAlertPage> {
  const res = await api.get(BASE, { ...(quiet as object), params: { page, pageSize } } as never);
  return (res.data as { data: InAppAlertPage }).data;
}

/** One alert with every result it covers. */
export async function getAlert(id: string): Promise<InAppAlert> {
  const res = await api.get(`${BASE}/${id}`);
  return (res.data as { data: InAppAlert }).data;
}

export async function markRead(id: string): Promise<void> {
  await api.post(`${BASE}/${id}/read`, {}, quiet);
}

export async function markAllRead(): Promise<number> {
  const res = await api.post(`${BASE}/read-all`, {});
  return ((res.data as { data?: { changed?: number } }).data?.changed) ?? 0;
}

/** Acknowledge one alert (audited server-side; idempotent). */
export async function acknowledge(id: string): Promise<void> {
  await api.post(`${BASE}/${id}/ack`, {});
}

/** One named pop-up configuration (Notification Center → Pop-up Settings). */
export interface PopupSetting extends AlertPopupSettings {
  id: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  /** How many notification rules are assigned to it. */
  ruleCount: number;
}

/** What the New / Edit dialog sends. */
export interface PopupSettingInput extends AlertPopupSettings {
  name: string;
  isDefault: boolean;
  isActive: boolean;
}

/** Why the server refused a delete (HTTP 409/400) — read from the error envelope. */
export interface PopupSettingRefusal { code: string; message: string; rules?: string[] }

const POPUP_BASE = '/api/v1/notifications/popup-settings';

export async function listPopupSettings(): Promise<PopupSetting[]> {
  const res = await api.get(POPUP_BASE);
  return (res.data as { data: PopupSetting[] }).data;
}

export async function createPopupSetting(input: PopupSettingInput): Promise<PopupSetting> {
  const res = await api.post(POPUP_BASE, input);
  return (res.data as { data: PopupSetting }).data;
}

export async function updatePopupSetting(id: string, input: PopupSettingInput): Promise<PopupSetting> {
  const res = await api.put(`${POPUP_BASE}/${id}`, input);
  return (res.data as { data: PopupSetting }).data;
}

/** Delete a setting. Deleting the Default needs `replacementDefaultId` (another Active setting). */
export async function deletePopupSetting(id: string, replacementDefaultId?: string): Promise<void> {
  await api.delete(`${POPUP_BASE}/${id}`, { params: replacementDefaultId ? { replacementDefaultId } : {} });
}

/** The server's refusal (code + message + rule list) from a failed call, if it sent one. */
export function popupRefusal(e: unknown): PopupSettingRefusal | undefined {
  const err = (e as { response?: { data?: { error?: { code?: string; message?: string; details?: { rules?: string[] } } } } })
    .response?.data?.error;
  return err?.message ? { code: err.code ?? '', message: err.message, rules: err.details?.rules } : undefined;
}
